import React, { useEffect, useState } from 'react';
import { groupAgentsService } from '../../../services/groupAgentsService';
import { userService } from '../../../services/userService';

interface Agent {
  id: number;
  full_name: string;
  email: string;
}

interface GroupAgentAvatarsProps {
  groupId: number;
  maxDisplay?: number;
}

export default function GroupAgentAvatars({ groupId, maxDisplay = 3 }: GroupAgentAvatarsProps) {
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const groupAgents = await groupAgentsService.getAgentsByGroupId(groupId);
        const agentDetails = await Promise.all(
          groupAgents.map(async (agent) => {
            const userDetails = await userService.getUser(agent.user_id);
            return {
              id: agent.id,
              full_name: userDetails.full_name,
              email: userDetails.email
            };
          })
        );
        setAgents(agentDetails);
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    };

    fetchAgents();
  }, [groupId]);

  const displayedAgents = agents.slice(0, maxDisplay);
  const remainingCount = Math.max(0, agents.length - maxDisplay);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  };

  const colors = [
    'bg-pink-600',
    'bg-purple-600',
    'bg-indigo-600',
    'bg-blue-600',
    'bg-green-600',
    'bg-yellow-600',
    'bg-red-600',
    'bg-teal-600',
    'bg-orange-600',
    'bg-cyan-600'
  ];

  const getColorForString = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="flex -space-x-2 relative z-0">
      {displayedAgents.map((agent, index) => (
        <div
          key={agent.id}
          className={`relative z-0 inline-flex items-center justify-center w-8 h-8 rounded-full ring-2 ring-white ${getColorForString(
            agent.full_name
          )}`}
          title={agent.full_name}
        >
          <span className="text-xs font-medium text-white">{getInitials(agent.full_name)}</span>
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className="relative z-0 inline-flex items-center justify-center w-8 h-8 rounded-full ring-2 ring-white bg-gray-400"
          title={`${remainingCount} more agents`}
        >
          <span className="text-xs font-medium text-white">+{remainingCount}</span>
        </div>
      )}
    </div>
  );
}
